import {getCart} from '@/cart/cart-store';
import PlanOptionButton from '@/components/buy-stack/PlanOptionButton';
import DeliverySelectorLabel from '@/experiments/product-select-v3/DeliverySelectorLabel';
import SelectorItemBox from '@/experiments/product-select-v3/SelectorItemBox';
import checkIcon from '@/icons/check-icon.svg';
import {getLocalizedStrings} from '@/localization/localization-store';
import {subscriptionUnit} from '@/localization/subscription-unit';
import {useLocale} from '@/localization/use-locale';
import {
  getPackVariant,
  getProductSelection,
  selectOption,
  selectPlan,
} from '@/product/product-selection';
import {PACK_SIZE} from '@/product/product-selection/pack-size';
import {
  discountPercentage,
  intervalFrequency,
  intervalUnit,
} from '@/selling-plan/utilities';
import {colorStyleMap} from '@/utilities/color-style-map';
import {batch} from '@preact/signals';
import {Plan} from '@rechargeapps/storefront-client';
import classNames from 'classnames';
import {useEffect, useState} from 'preact/hooks';

export default PlanOptionSelectorBox;

function elementAtOrLast<T>(array: T[], index: number): T {
  return array[Math.min(index, array.length - 1)];
}

function isNotEmpty<T>(maybeArray: T[] | null | undefined): maybeArray is T[] {
  return Array.isArray(maybeArray) && maybeArray.length > 0;
}

function PlanOptionSelectorBox({
  formatMoneyWithDecimals = false,
  bullets,
  onClickFrequencyHelper,
  savingsPercentages,
  displayPrices,
  compareAtPrices,
}: PlanOptionSelectorBoxProps) {
  const {monthlyPlans, packOptions, product, selectedPlan, selectedVariant} =
    getProductSelection();

  const [selectedMonthlyPlan, setSelectedMonthlyPlan] = useState<Plan | null>(
    null
  );

  const strings = getLocalizedStrings();
  const bulletPoints = bullets ?? [
    strings.buyStackSubscribeBulletFirst ?? 'No Set Term, Cancel Anytime',
    strings.buyStackSubscribeBulletSecond ??
      '100% Risk Free, Money Back Guarantee',
  ];

  const frequencyHelperLabel =
    strings.buyStackFrequencyHelperLabel ?? 'Which frequency is right for me?';
  const deliveryLabel = strings.buyStackDeliveryLabel ?? 'DELIVERY EVERY';
  const subscribeSaveLabel =
    strings.buyStackSubscribeSaveLabel ?? 'Subscribe & SAVE';
  const subscribeLabel = subscribeSaveLabel.split(' ').slice(0, -1).join(' ');
  const saveLabel = subscribeSaveLabel.split(' ').slice(-1).join(' ');
  const badgeLabel = strings.buyStackBestValue ?? 'Best Value';

  const isSoldOut = !getPackVariant(packOptions[0])?.available;

  useEffect(() => {
    if (selectedPlan && monthlyPlans.includes(selectedPlan)) {
      setSelectedMonthlyPlan(selectedPlan);
    } else {
      setSelectedMonthlyPlan(monthlyPlans[0] ?? null);
    }
  }, [monthlyPlans, selectedPlan]);

  const planIndex = selectedMonthlyPlan
    ? monthlyPlans.indexOf(selectedMonthlyPlan)
    : 0;

  // The first variant is assumed to be the 1 pack
  // The selling plan discount is applied to get the sub price
  const selectedDiscount = selectedMonthlyPlan
    ? discountPercentage(selectedMonthlyPlan)
    : 0;
  const variantPrice = product?.variants[0].price ?? 0;
  const originalPrice = isNotEmpty(compareAtPrices)
    ? elementAtOrLast(compareAtPrices, planIndex)
    : variantPrice;

  const selectedDiscountedPrice = originalPrice * (1 - selectedDiscount);
  const displayPrice = isNotEmpty(displayPrices)
    ? elementAtOrLast(displayPrices, planIndex)
    : selectedDiscountedPrice;

  const calculatedSavingsLabel =
    selectedDiscount > 0
      ? `${saveLabel} ${selectedDiscount * 100}%`
      : saveLabel;
  const savingsLabel = isNotEmpty(savingsPercentages)
    ? elementAtOrLast(savingsPercentages, planIndex)
    : calculatedSavingsLabel;

  const selectedColor = selectedVariant?.accentColor ?? 'primary-blue';
  const colorStyle = colorStyleMap.get(selectedColor);
  const cart = getCart();
  const currency = cart?.currency ?? 'USD';
  const locale = useLocale();

  const handleClickMonthly = () => {
    batch(() => {
      if (packOptions.length > 0) {
        selectOption({name: PACK_SIZE, value: packOptions[0]});
      }

      if (selectedMonthlyPlan) {
        selectPlan(selectedMonthlyPlan);
      }
    });
  };

  const handleClickMonthlyPlan = (plan: Plan) => {
    setSelectedMonthlyPlan(plan);
    selectPlan(plan);
  };

  const handleClickFrequencyHelper = (e: MouseEvent) => {
    e.preventDefault();
    onClickFrequencyHelper?.call(this);
  };

  return monthlyPlans.length > 0 ? (
    <SelectorItemBox
      dataTestid="recurring-purchase"
      badgeLabel={badgeLabel.toUpperCase()}
      color={selectedColor}
      showBackgroundColor={true}
      label={
        <DeliverySelectorLabel
          compareAtPrice={originalPrice}
          currency={currency}
          locale={locale}
          formatMoneyWithDecimals={formatMoneyWithDecimals}
          price={displayPrice}
        >
          <span className={isSoldOut ? 'line-through' : ''}>
            {subscribeLabel}{' '}
            <span
              className={classNames(
                'font-semibold',
                colorStyle?.text ?? 'text-logo-lime'
              )}
            >
              {savingsLabel}
            </span>
          </span>
        </DeliverySelectorLabel>
      }
      isSelected={!!selectedPlan && monthlyPlans.includes(selectedPlan)}
      onClick={handleClickMonthly}
      footer={
        <div className="grid gap-y-2 pb-3">
          {
            <p
              className="cursor-pointer underline"
              onClick={handleClickFrequencyHelper}
            >
              {frequencyHelperLabel}
            </p>
          }
          <p className="text-center text-xs font-medium uppercase leading-6">
            {document.body
              .computedStyleMap()
              .get('--bs-1-pricing-bullet-test') && (
              <>
                <img
                  src={checkIcon}
                  alt="Check Icon"
                  loading="lazy"
                  height="16"
                  width="16"
                  className="inline-block align-top"
                />
                <span className="before:content-[var(--bs-1-pricing-bullet-test)]"></span>
                <br />
              </>
            )}
            {bulletPoints.map((point) => (
              <>
                <img
                  src={checkIcon}
                  alt="Check Icon"
                  loading="lazy"
                  height="16"
                  width="16"
                  className="inline-block align-top"
                />
                {point}
                <br />
              </>
            ))}
          </p>
        </div>
      }
    >
      <div className="grid gap-y-3">
        {monthlyPlans.length > 1 && (
          <>
            <div className="flex gap-3">
              {monthlyPlans.map((plan) => (
                <PlanOptionButton
                  dataTestId={`order-interval-${intervalFrequency(plan)}`}
                  key={plan.id}
                  isSelected={plan.id === selectedMonthlyPlan?.id}
                  onClick={() => handleClickMonthlyPlan(plan)}
                  color={selectedColor}
                >
                  {intervalFrequency(plan)}
                </PlanOptionButton>
              ))}
            </div>
            {selectedMonthlyPlan && (
              // eslint-disable-next-line tailwindcss/no-custom-classname
              <span className="flex items-center justify-center gap-x-1 text-center text-sm font-semibold uppercase text-neutral-dark-gray xs:text-lg">
                {deliveryLabel}{' '}
                {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
                <span
                  className={classNames(
                    'rounded  px-2 text-bright-white',
                    colorStyle?.background ?? 'bg-logo-lime'
                  )}
                >
                  {intervalFrequency(selectedMonthlyPlan)}
                </span>{' '}
                {subscriptionUnit(
                  intervalUnit(selectedMonthlyPlan),
                  strings,
                  intervalFrequency(selectedMonthlyPlan) !== 1
                )}
              </span>
            )}
          </>
        )}
      </div>
    </SelectorItemBox>
  ) : (
    <></>
  );
}

export type PlanOptionSelectorBoxProps = {
  formatMoneyWithDecimals?: boolean;
  bullets?: string[];
  onClickFrequencyHelper?: () => void;
} & PlanOptionPriceOverrides;

export type PlanOptionPriceOverrides = Partial<{
  savingsPercentages: string[] | null;
  displayPrices: number[] | null;
  compareAtPrices: number[] | null;
}>;
